import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLanguageFromPath } from "@/lib/i18n";

/**
 * Keeps `LanguageContext` aligned with the URL on every navigation.
 * URL is the single authoritative source of language: `/pl/...` → PL,
 * `/de/...` → DE, everything else → EN (prefix-less URLs are EN by
 * design - the Vercel Edge Middleware in `middleware.ts` handles
 * per-user redirects server-side on cold visits to `/` before this
 * code ever runs, so the client must not second-guess the URL).
 *
 * Firing unconditionally (no "skip prefix-less" special case) fixes a
 * race where a stale pathname during navigation could undo a user-
 * initiated switch: switching PL → EN calls `navigate("/")`, but the
 * very next render still sees `pathname === "/pl"` for one tick; a
 * conditional sync would flip state back to "pl" and then - when the
 * pathname finally catches up to "/" - refuse to act (prefix-less skip),
 * stranding the user on PL. Treating prefix-less as an explicit "en"
 * sync makes the final render deterministic.
 *
 * Must be mounted inside `<BrowserRouter>`. Never persists to localStorage
 * - URL is authoritative.
 */
export const LanguageUrlSync = () => {
  const { language, setLanguageFromUrl } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    // Auth-gated routes (/account/*) live only on top-level - URL is NOT
    // authoritative there. `LanguagePersistenceGuard` applies the saved
    // preference instead. Skip in lockstep with that guard.
    if (/^\/account(\/|$)/.test(pathname)) return;
    // URL is authoritative. Prefix-less URLs are EN by design.
    const fromUrl = getLanguageFromPath(pathname) ?? "en";
    if (fromUrl !== language) {
      setLanguageFromUrl(fromUrl);
    }
  }, [pathname, language, setLanguageFromUrl]);

  return null;
};

export default LanguageUrlSync;
